import { FileText, LayoutDashboard, type LucideIcon, PenLine, Plug, Send } from 'lucide-react';

import { Page } from '@/components/page.tsx';
import { StartTile, type StartTileDestination } from '@/components/start/start-tile.tsx';
import { Heading1 } from '@/components/text/heading1.tsx';
import { Heading2 } from '@/components/text/heading2.tsx';

import { AppRoute } from '@/constants/app-routes.ts';
import { SCRIBE_URL } from '@/constants/external-links.ts';
import { FeatureFlag } from '@/constants/feature-flags.ts';

import { useFeatureFlag } from '@/hooks/use-feature-flag.ts';

interface Tile {
    icon: LucideIcon;
    title: string;
    description: string;
    destination: StartTileDestination;
    // When set, the tile greys out while the flag is off.
    flag?: FeatureFlag;
}

// Top row shows three tiles, bottom row shows the remaining two.
const TOP_TILES: Tile[] = [
    {
        icon: LayoutDashboard,
        title: 'CareView',
        description: 'Praxis-Dashboards und Kennzahlen auf einen Blick.',
        destination: { to: AppRoute.CAREVIEW },
        flag: FeatureFlag.IS_CAREVIEW_ENABLED,
    },
    {
        icon: Send,
        title: 'Recall',
        description: 'Automatisierte Patienten-Recalls für Ihre Praxis.',
        destination: { to: AppRoute.RECALL },
        flag: FeatureFlag.IS_RECALL_ENABLED,
    },
    {
        icon: PenLine,
        title: 'Scribe',
        description: 'KI-gestützte Dokumentation – in einem neuen Tab öffnen.',
        destination: { href: SCRIBE_URL },
    },
];

const BOTTOM_TILES: Tile[] = [
    {
        icon: Plug,
        title: 'Connect',
        description: 'Automatisierte KI-Telefonie für Ihre Praxis verwalten.',
        destination: { to: AppRoute.CONNECT },
        flag: FeatureFlag.IS_CONNECT_ENABLED,
    },
    {
        icon: FileText,
        title: 'Patientenblick',
        description: 'Dokumente und Scans Ihrer Patienten auslesen.',
        destination: { to: AppRoute.EXTRACTION },
        flag: FeatureFlag.IS_PATIENTVIEW_ENABLED,
    },
];

const isTileDisabled = (tile: Tile, enabledByFlag: Partial<Record<FeatureFlag, boolean>>): boolean =>
    tile.flag !== undefined && !enabledByFlag[tile.flag];

export const StartPage = () => {
    // Read the gated flags once; a tile is disabled when its flag is off.
    // Mirrors FeatureGuard (keys off the resolved value, not the loading flag).
    const enabledByFlag: Partial<Record<FeatureFlag, boolean>> = {
        [FeatureFlag.IS_PATIENTVIEW_ENABLED]: useFeatureFlag(FeatureFlag.IS_PATIENTVIEW_ENABLED).value,
        [FeatureFlag.IS_RECALL_ENABLED]: useFeatureFlag(FeatureFlag.IS_RECALL_ENABLED).value,
        [FeatureFlag.IS_CAREVIEW_ENABLED]: useFeatureFlag(FeatureFlag.IS_CAREVIEW_ENABLED).value,
        [FeatureFlag.IS_CONNECT_ENABLED]: useFeatureFlag(FeatureFlag.IS_CONNECT_ENABLED).value,
    };

    return (
        <Page className="flex flex-col">
            <div className="mx-auto w-full max-w-[1100px] space-y-10 p-6">
                <section aria-label="start-headings">
                    <div className="space-y-2">
                        <Heading1 className="text-3xl text-foreground">Willkommen</Heading1>
                        <Heading2 className="mt-1 text-base text-muted-foreground">
                            Wählen Sie einen Bereich, um loszulegen.
                        </Heading2>
                    </div>
                </section>
                <section aria-label="start-tiles" className="space-y-6">
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {TOP_TILES.map((tile) => (
                            <StartTile key={tile.title} {...tile} disabled={isTileDisabled(tile, enabledByFlag)} />
                        ))}
                    </div>
                    <div className="mx-auto grid grid-cols-1 gap-6 sm:grid-cols-2 lg:max-w-[calc(66.666%-1rem)]">
                        {BOTTOM_TILES.map((tile) => (
                            <StartTile key={tile.title} {...tile} disabled={isTileDisabled(tile, enabledByFlag)} />
                        ))}
                    </div>
                </section>
            </div>
        </Page>
    );
};
